// ダッシュボード表示部品の共通コンポーネント（S-055-1 / DEC-053 案B）。
//
// ProjectDashboardPage にインライン定義されていた純表示部品を抽出し、
// 「プロジェクト別詳細（ProjectDashboardPage）」と「会社別詳細（CompanyTimelinePage）」の
// 双方から data-props のみで流用できるようにする。
//   - Timeline      : ステータスバッジ付き縦線タイムライン
//   - AISection     : AI 派生セクションのロックラッパ
//   - SummaryCard   : サマリ本文＋stale バナー＋再計算 CTA
//   - Decisions     : 決定事項
//   - KeyPersons    : キーパーソン
// データ取得（Firestore 購読・chat 呼出）は各ページ側の責務。ここは表示専任。
import React from 'react';
import { Btn, Card, Icon, LockVeil, Pill, SectionLabel, SourceChip } from './primitives';

// ─── 日時フォーマッタ（両ページ共通） ───
export const toDate = (ts: any): Date | null => {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d;
};

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];
export const formatDateShort = (ts: any): string => {
  const d = toDate(ts);
  if (!d) return '';
  return `${d.getMonth() + 1}/${d.getDate()} (${WEEKDAYS[d.getDay()]})`;
};
export const formatTime = (ts: any): string => {
  const d = toDate(ts);
  if (!d) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
export const formatDate = (ts: any): string => {
  const d = toDate(ts);
  if (!d) return '';
  return d.toLocaleDateString('ja-JP', { year: 'numeric', month: 'short', day: 'numeric' });
};
export const formatDateTime = (ts: any): string => {
  const d = toDate(ts);
  if (!d) return '';
  return d.toLocaleString('ja-JP', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};
export const formatDuration = (seconds?: number): string => {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}分${s > 0 ? s + '秒' : ''}`;
};

// ─── 共有型 ───
export interface TimelineMeeting {
  id: string;
  title: string;
  recordedAt?: any;
  duration?: number;
  status?: string;
  deletedAt?: any;
  channel?: string;
  excerpt?: string;
  summary?: string;
  participants?: string[];
}

export interface DashboardDecision {
  text: string;
  sourceMeetingId?: string;
  sourceMeetingTitle?: string;
  decidedAt?: string;
}
export interface DashboardKeyPerson {
  name: string;
  role?: string;
  appearanceCount?: number;
}

// キーパーソンアバターの色（プロトタイプの teal/amber/success ローテーション）。
const PERSON_COLORS = ['var(--teal-400)', 'var(--amber-500)', 'var(--success)'];

// ─── タイムライン ───
const STATUS_MAP: Record<string, { kind: 'success' | 'teal' | 'danger' | 'neutral'; label: string; pulse?: boolean }> = {
  completed: { kind: 'success', label: '完了' },
  processing: { kind: 'teal', label: '処理中', pulse: true },
  failed: { kind: 'danger', label: '失敗' },
};

function channelIcon(channel?: string): string {
  if (!channel) return 'video';
  if (channel.includes('オンライン')) return 'video';
  if (channel.includes('社内')) return 'building';
  return 'map-pin';
}

export const Timeline: React.FC<{
  meetings: TimelineMeeting[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  onOpenMeeting: (m: TimelineMeeting) => void;
  emptyTitle?: string;
  emptyHint?: string;
  // Web で議事録の「中身」が閲覧不可なプラン（free / light）のときに、各カードの
  // プレビュー本文（excerpt / summary）だけをぼかす。会議タイトル・日時・相手先等の
  // メタ情報は読める状態のまま（DEC-059: 「何の会議があるか」は見せ「中身」は隠す）。
  lockExcerpt?: boolean;
}> = ({ meetings, loading, error, totalCount, onOpenMeeting, emptyTitle, emptyHint, lockExcerpt }) => {
  return (
    <div>
      <SectionLabel icon="git-commit-horizontal" count={`${totalCount}件`}>タイムライン</SectionLabel>

      {loading && <div style={{ padding: 24, textAlign: 'center', color: 'var(--fg-tertiary)', font: 'var(--type-body)' }}>読み込み中...</div>}
      {error && (
        <div style={{ padding: '12px 16px', borderRadius: 'var(--r-2)', background: 'var(--danger-fill)', border: '1px solid var(--danger-line)', color: 'var(--danger)', font: 'var(--type-caption)' }}>
          {error}
        </div>
      )}
      {!loading && !error && meetings.length === 0 && (
        <Card style={{ textAlign: 'center', padding: 40 }}>
          <Icon name="mic" size={28} style={{ color: 'var(--fg-tertiary)', marginBottom: 10 }} />
          <p style={{ font: 'var(--type-body)', color: 'var(--fg-secondary)', marginBottom: 4 }}>{emptyTitle || '会議がありません'}</p>
          <p style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)' }}>{emptyHint || 'Androidアプリから録音してください'}</p>
        </Card>
      )}

      {meetings.length > 0 && (
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', left: 7, top: 6, bottom: 6, width: 2, background: 'var(--border-strong)' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {meetings.map((m, i) => {
              const s = STATUS_MAP[m.status || ''] || { kind: 'neutral' as const, label: m.status || '不明' };
              const latest = i === 0;
              const clickable = m.status === 'completed';
              const excerpt = m.excerpt || m.summary;
              const persons = m.participants;
              const dateStr = formatDateShort(m.recordedAt);
              const timeStr = formatTime(m.recordedAt);
              return (
                <div key={m.id} style={{ position: 'relative', paddingLeft: 32 }}>
                  <div style={{
                    position: 'absolute', left: 0, top: 4, width: 16, height: 16, borderRadius: 999,
                    background: latest ? 'var(--cta)' : 'var(--bg-elevated)',
                    border: '2px solid ' + (latest ? 'var(--cta)' : 'var(--accent)'),
                    boxShadow: latest ? '0 0 0 4px var(--cta-fill)' : 'none',
                  }} />
                  <Card hoverable={clickable} onClick={clickable ? () => onOpenMeeting(m) : undefined} pad={16}
                    style={{ display: 'flex', flexDirection: 'column', gap: 8, opacity: m.status === 'failed' ? 0.6 : 1 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                      <span className="mono" style={{ font: 'var(--type-mono)', color: 'var(--fg-tertiary)', fontSize: 12 }}>
                        {dateStr || formatDateTime(m.recordedAt)} {timeStr}
                      </span>
                      <div style={{ flex: 1 }} />
                      <Pill kind={s.kind} pulse={s.pulse}>{s.label}</Pill>
                    </div>
                    <div style={{ font: '500 15px/1.4 var(--font-sans)', color: 'var(--fg)' }}>{m.title || '無題の会議'}</div>
                    {excerpt && (
                      <p style={{
                        font: 'var(--type-caption)', color: 'var(--fg-secondary)', fontSize: 12.5, lineHeight: 1.55,
                        ...(lockExcerpt ? { filter: 'blur(4px)', userSelect: 'none' as const } : null),
                      }}
                        aria-hidden={lockExcerpt || undefined}
                      >
                        {excerpt}
                      </p>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 2 }}>
                      {m.channel && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, font: 'var(--type-caption)', color: 'var(--fg-tertiary)' }}>
                          <Icon name={channelIcon(m.channel)} size={13} />{m.channel}
                        </span>
                      )}
                      {m.duration ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, font: 'var(--type-caption)', color: 'var(--fg-tertiary)' }}>
                          <Icon name="clock" size={13} />{formatDuration(m.duration)}
                        </span>
                      ) : null}
                      {persons && persons.length > 0 && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, font: 'var(--type-caption)', color: 'var(--fg-tertiary)' }}>
                          <Icon name="user" size={13} />{persons.join('・')}
                        </span>
                      )}
                    </div>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── AIセクションのラッパ（ロック対応） ───
export const AISection: React.FC<{ locked: boolean; onUpsell: () => void; message: string; children: React.ReactNode }> = ({ locked, onUpsell, message, children }) => (
  <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 'var(--r-3)' }}>
    <div style={{ filter: locked ? 'blur(7px) saturate(0.8)' : 'none', pointerEvents: locked ? 'none' : 'auto', userSelect: locked ? 'none' : 'auto' }}>
      {children}
    </div>
    {locked && <LockVeil onUpsell={onUpsell} message={message} />}
  </div>
);

// ─── サマリ（stale バナー + 再計算 CTA） ───
export const SummaryCard: React.FC<{
  hasDashboard: boolean;
  summaryText: string;
  computedAt: any;
  stale: boolean;
  recomputing: boolean;
  recomputeError: string | null;
  onRecompute: () => void;
  locked: boolean;
  title?: string;
  emptyText?: string;
}> = ({ hasDashboard, summaryText, computedAt, stale, recomputing, recomputeError, onRecompute, locked, title, emptyText }) => {
  return (
    <Card>
      <SectionLabel icon="sparkles"
        right={!locked && hasDashboard ? (
          <span style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)' }}>
            {formatDateTime(computedAt)} 時点
          </span>
        ) : undefined}>
        {title || '案件サマリ'}
      </SectionLabel>

      {!hasDashboard ? (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 12, padding: '8px 0',
        }}>
          <p style={{ font: 'var(--type-body)', color: 'var(--fg-secondary)', fontSize: 14, lineHeight: 1.7 }}>
            {emptyText || 'この案件のサマリはまだ生成されていません。会議が揃ったら最新の議事録からサマリ・決定事項・キーパーソンを生成できます。'}
          </p>
          <Btn kind="accent" size="sm" icon={recomputing ? 'loader' : 'sparkles'} iconSpin={recomputing} onClick={onRecompute} disabled={recomputing}>
            {recomputing ? '生成中…' : 'サマリを生成'}
          </Btn>
          {recomputeError && <span style={{ font: 'var(--type-caption)', color: 'var(--danger)' }}>{recomputeError}</span>}
        </div>
      ) : (
        <>
          {stale && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', marginBottom: 14,
              borderRadius: 'var(--r-2)', background: 'var(--accent-fill-faint)', border: '1px solid var(--accent-line)',
            }}>
              <Icon name="info" size={15} style={{ color: 'var(--accent)', flexShrink: 0 }} />
              <span style={{ flex: 1, font: 'var(--type-caption)', color: 'var(--fg-secondary)' }}>
                新しい会議があります。サマリを最新化できます。
              </span>
              <Btn kind="accent" size="sm" icon={recomputing ? 'loader' : 'refresh-cw'} iconSpin={recomputing} onClick={onRecompute} disabled={recomputing}>
                {recomputing ? '最新化中…' : '最新化'}
              </Btn>
            </div>
          )}
          <p style={{ font: 'var(--type-body)', color: 'var(--fg-secondary)', fontSize: 14, lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>
            {summaryText || '（サマリ本文がありません）'}
          </p>
          {recomputeError && <div style={{ marginTop: 10, font: 'var(--type-caption)', color: 'var(--danger)' }}>{recomputeError}</div>}
        </>
      )}
    </Card>
  );
};

// ─── 決定事項 ───
export const Decisions: React.FC<{ decisions: DashboardDecision[]; onJump: (meetingId?: string) => void }> = ({ decisions, onJump }) => (
  <Card>
    <SectionLabel icon="gavel" count={`${decisions.length}件`}>決定事項</SectionLabel>
    {decisions.length === 0 ? (
      <div style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)', textAlign: 'center', padding: 12 }}>
        確定した決定事項はまだありません
      </div>
    ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {decisions.map((d, i) => {
          const label = d.sourceMeetingTitle || (d.decidedAt ? `${d.decidedAt} の会議` : '出典の会議');
          return (
            <div key={i} style={{ display: 'flex', gap: 12 }}>
              <Icon name="check" size={16} style={{ color: 'var(--success)', marginTop: 3, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ font: 'var(--type-body)', color: 'var(--fg)', fontSize: 14, marginBottom: 6 }}>{d.text}</div>
                <SourceChip label={label} onClick={d.sourceMeetingId ? () => onJump(d.sourceMeetingId) : undefined} />
              </div>
            </div>
          );
        })}
      </div>
    )}
  </Card>
);

// ─── キーパーソン ───
export const KeyPersons: React.FC<{ persons: DashboardKeyPerson[] }> = ({ persons }) => (
  <Card>
    <SectionLabel icon="users" count={`${persons.length}名`}>キーパーソン</SectionLabel>
    {persons.length === 0 ? (
      <div style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)', textAlign: 'center', padding: 12 }}>
        キーパーソンはまだ整理されていません
      </div>
    ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {persons.map((p, i) => {
          const color = PERSON_COLORS[i % PERSON_COLORS.length];
          const initial = (p.name || '?').trim().charAt(0) || '?';
          return (
            <div key={i} style={{ display: 'flex', gap: 12 }}>
              <div style={{
                width: 38, height: 38, borderRadius: 999, flexShrink: 0,
                background: `color-mix(in srgb, ${color} 18%, transparent)`,
                border: `1.5px solid ${color}`, color,
                display: 'grid', placeItems: 'center', font: '700 15px/1 var(--font-sans)',
              }}>{initial}</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ font: '500 14px/1.3 var(--font-sans)', color: 'var(--fg)' }}>{p.name}</span>
                  {p.role && <span style={{ font: 'var(--type-caption)', color: 'var(--accent)' }}>{p.role}</span>}
                </div>
                {p.appearanceCount != null && p.appearanceCount > 0 && (
                  <div style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)', marginTop: 1 }}>
                    {p.appearanceCount}件の会議に登場
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    )}
  </Card>
);
